/**
 * Chat history export — the row model (PLAN §8).
 *
 * `toExportRow` is a WHITELIST projection of a wire message: it never spreads
 * `metadata`. The export endpoint already redacts secrets and share tokens on
 * the server (D13/D19); this is the second, independent guard, so a secret's
 * ciphertext, its wrapped keys, a share token or an attachment URL cannot
 * reach a file even if the server shape changes. A vault secret contributes
 * its LABEL only.
 */

export const EXPORT_FORMATS = {
  markdown: { ext: 'md', mime: 'text/markdown', filters: [{ name: 'Markdown', extensions: ['md', 'markdown'] }] },
  json: { ext: 'json', mime: 'application/json', filters: [{ name: 'JSON', extensions: ['json'] }] },
  text: { ext: 'txt', mime: 'text/plain', filters: [{ name: 'Text', extensions: ['txt'] }] },
  html: { ext: 'html', mime: 'text/html', filters: [{ name: 'HTML', extensions: ['html', 'htm'] }] },
};

export const EXPORT_FORMAT_ORDER = ['markdown', 'json', 'text', 'html'];

const str = (v) => (typeof v === 'string' ? v : null);
const num = (v) => (Number.isFinite(v) ? v : null);

function toIso(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function shareOf(kind, md) {
  if (kind === 'ref') {
    return { type: str(md.type) || 'reference', label: str(md.label), legacy: true };
  }
  const type = str(md.share_type) || 'item';
  if (type === 'task') return { type: 'task' };
  if (type === 'appointment') {
    return {
      type: 'appointment',
      title: str(md.title),
      start: str(md.start),
      end: str(md.end),
      all_day: md.all_day === true,
      location: str(md.location),
    };
  }
  const share = { type, label: str(md.label) };
  if (str(md.method)) share.method = md.method;
  if (num(md.item_count) != null) share.item_count = md.item_count;
  if (str(md.workspace_name)) share.workspace_name = md.workspace_name;
  return share;
}

export function toExportRow(m) {
  const md = m?.metadata && typeof m.metadata === 'object' ? m.metadata : {};
  const kind = m?.kind || 'text';
  const body = typeof m?.body === 'string' ? m.body : '';
  const row = {
    seq: m?.server_seq ?? null,
    id: m?.id ?? null,
    at: toIso(m?.inserted_at),
    sender: { id: m?.sender_id ?? m?.sender?.id ?? null, name: str(m?.sender?.name) },
    kind,
    text: '',
    edited: Boolean(m?.edited_at),
    forwarded: md.forwarded === true,
    reply: md.reply_to
      ? { sender_name: str(md.reply_to.sender_name), body: str(md.reply_to.body) || '' }
      : null,
    attachment: null,
    share: null,
    secret: null,
    code: null,
    reactions: (Array.isArray(m?.reactions) ? m.reactions : []).map((r) => ({
      emoji: str(r?.emoji) || '',
      users: (Array.isArray(r?.users) ? r.users : []).map((u) => str(u?.name) || '').filter(Boolean),
      count: num(r?.count) ?? (Array.isArray(r?.users) ? r.users.length : 0),
    })),
  };
  if (kind === 'text') row.text = body;
  else if (kind === 'code') row.code = { language: str(md.language) || 'plaintext', source: body };
  else if (kind === 'image' || kind === 'file') {
    row.text = body;
    row.attachment = { kind, name: str(md.name), size: num(md.size), mime: str(md.mime) };
  } else if (kind === 'share' || kind === 'ref') row.share = shareOf(kind, md);
  else if (kind === 'secret') row.secret = { label: str(md.label), item_type: str(md.item_type) };
  else row.text = body;
  return row;
}

function slugify(name) {
  return (
    (name || 'chat')
      .trim()
      .toLowerCase()
      .replace(/[^\w.\- ]+/g, '')
      .replace(/\s+/g, '-')
      .replace(/^-+|-+$/g, '') || 'chat'
  );
}

/** `kenan-kobic-chat-2026-09-23.md` — local date, extension per format. */
export function exportFileName(channelName, format, date = new Date()) {
  const p = (n) => String(n).padStart(2, '0');
  const day = `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}`;
  const ext = (EXPORT_FORMATS[format] || EXPORT_FORMATS.markdown).ext;
  return `${slugify(channelName)}-chat-${day}.${ext}`;
}
